import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CNConsultation } from 'src/app/models/cnconsultation';
import { CNContactRXImage } from 'src/app/models/cncontactrximage';
import { CNContactRXSerie } from 'src/app/models/cncontactrxserie';
import { CNPatient } from 'src/app/models/cnpatient';
import { ImagingService } from 'src/app/services/imaging.service';
import { BecareConfigService } from 'src/app/services/becare-config.service';
import { WaitingDialogComponent } from '../../../dialogs/waiting-dialog/waiting-dialog.component';

/**
 * RX serie viewer. Extracted from RxSerie2DialogComponent so the same UI can be
 * hosted either in a dialog or directly in a page. The dialog is now a thin
 * wrapper around this component.
 *
 * The Viewer app carries a sibling of this component at the same path; the two are
 * kept in step by ts/tools/sync-viewer.mjs. The template and stylesheet are meant
 * to stay identical - only this file legitimately differs, because the Viewer has
 * no authenticated session and resolves images and the PACS URL differently.
 */
@Component({
  selector: 'app-rx-serie-2',
  templateUrl: './rx-serie-2.component.html',
  styleUrls: ['./rx-serie-2.component.scss']
})
export class RxSerie2Component implements OnInit {

  @Input() patient: CNPatient;
  @Input() contact: CNConsultation;

  /** Shows the header close button and emits `closed`; set by the dialog host. */
  @Input() dismissible = false;

  @Output() closed = new EventEmitter<void>();

  /**
   * The footer's "Télécharger le Protocole" action. Emitted rather than handled
   * here because the report lives with the host: Becare9 has its own report
   * dialogs, the Viewer opens the shared-link PDF. Unbound hosts get today's
   * behaviour, which is none.
   */
  @Output() protocolRequested = new EventEmitter<void>();

  public selectedImage: CNContactRXImage;
  public allImages: CNContactRXImage[] = [];

  private theSeries: CNContactRXSerie[] = [];

  constructor(
    private dialog: MatDialog,
    private imaging: ImagingService,
    private becareConfig: BecareConfigService
  ) { }

  @Input() set series(theSeries: CNContactRXSerie[]) {
    this.theSeries = theSeries || [];
    this.indexImages();
  }

  get series(): CNContactRXSerie[] {
    return this.theSeries;
  }

  ngOnInit(): void {
    const serie = this.theSeries[0];
    if (!serie) {
      return;
    }
    this.imaging.rxSerieImages(this.patient, this.contact, serie).subscribe(resultSerie => {
      this.theSeries = [resultSerie];
      this.selectedImage = undefined;
      this.indexImages();
    });
  }

  /** The serie owning the selected image, for the sidebar and the PACS call. */
  get currentSerie(): CNContactRXSerie {
    const owner = this.theSeries.find(serie => serie.images.indexOf(this.selectedImage) !== -1);
    return owner ? owner : this.theSeries[0];
  }

  get imageCount(): number {
    return this.allImages.length;
  }

  /** 1-based position of the selected image, for the overlay. */
  get imageIndex(): number {
    return this.selectedImage ? this.allImages.indexOf(this.selectedImage) + 1 : 0;
  }

  get contactDate(): Date {
    return this.contact ? this.contact.date : undefined;
  }

  openDicomViewer(): void {
    const serie = this.currentSerie;
    if (!serie) {
      return;
    }
    const waitDialog = this.dialog.open(WaitingDialogComponent, {
      data: { title: 'Envoi en cours ...', error: undefined, result: undefined }
    });
    this.imaging.sendToPacs(serie).subscribe(studies => {
      this.becareConfig.globalProfileValues('ViewerRX', 'rxviewer').subscribe(result => {
        window.open(result.values[0] + '/viewer/?StudyInstanceUIDs=' + studies[0], '_blank');
        setTimeout(() => waitDialog.close(), 2000);
      });
    }, () => {
      setTimeout(() => waitDialog.close(), 2000);
    });
  }

  /** Zip of every DICOM file of the consultation; the session cookie authenticates the download. */
  exportDicom(): void {
    if (!this.contact) {
      return;
    }
    window.open('/api/v1/fr/rx/consultation/' + this.contact.id + '/dicom', '_blank');
  }

  showImage(image: CNContactRXImage): void {
    this.selectedImage = image;
  }

  close(): void {
    this.closed.emit();
  }

  private indexImages(): void {
    this.allImages = [];
    this.theSeries.forEach(serie => {
      this.allImages = this.allImages.concat(serie.images);
    });
    if (!this.selectedImage && this.allImages.length !== 0) {
      this.selectedImage = this.allImages[0];
    }
  }
}
